import React from 'react';
import PropTypes from 'prop-types';

import { FormattedHTMLMessage } from 'component-utilities/intl';
import displayName from 'component-lib/displayName';
import alert from './Dialog/alert.png';
import RetinaImage from './RetinaImage';
import Logo from './AppHeader/Logo';
import AppSwitcher from './AppHeader/AppSwitcher';
import HelpMenu from './AppHeader/HelpMenu';
import Settings from './AppHeader/Settings';
import AccountAndUserMenu from './AppHeader/AccountAndUserMenu';

import appSwitcherTabPropTypes from './AppHeader/utils/appSwitcherTabPropTypes';
import generateMenuPropTypes from './AppHeader/utils/generateMenuPropTypes';
import userPropTypes from './AppHeader/propTypes/user';
import settingsPropTypes from './AppHeader/propTypes/settings';

import { HELP_MENU, ACCOUNT_AND_USER_MENU } from './AppHeader/config';

import styles from './AppHeader/AppHeader.scss';

const menuPropTypes = [
    ['helpMenu', HELP_MENU],
    ['accountAndUserMenu', ACCOUNT_AND_USER_MENU],
].reduce(
    (all, [key, menu]) => ({
        ...all,
        [key]: generateMenuPropTypes(menu.ITEMS),
    }),
    {},
);

menuPropTypes.accountAndUserMenu = {
    ...menuPropTypes.accountAndUserMenu,
    user: userPropTypes,
    pendingJobsCount: PropTypes.number,
};

menuPropTypes.settings = settingsPropTypes;

export default function AppHeader(props) {
    const {
        onLogoClick,
        activeApp,
        isStaticActiveApp,
        analyticsBeta,
        customNavLogo,
        visibleMenus,
        buttons,
        hasEndUserRole,
        isOnlyEndUser,
        pendingJobsCount,
    } = props;

    const isAccountSettingsMenuVisible =
    typeof visibleMenus.other !== 'undefined' && visibleMenus.other.indexOf('account_settings') !== -1;

    let headerButtons;
    if (buttons.length) {
        headerButtons = buttons;
    } else if (isAccountSettingsMenuVisible) {
        headerButtons = [
            [HelpMenu, 'helpMenu'],
            [Settings, 'settings'],
            [AccountAndUserMenu, 'accountAndUserMenu'],
        ];
    } else {
        headerButtons = [[HelpMenu, 'helpMenu'], [AccountAndUserMenu, 'accountAndUserMenu']];
    }

    const appSwitcherProps = Object.keys(appSwitcherTabPropTypes).reduce(
        (all, key) => ({ ...all, [key]: props[key] }),
        {},
    );

    const menuProps = Object.keys(menuPropTypes).reduce((all, menu) => {
        const propsForType = menuPropTypes[menu];

        return {
            ...all,
            [menu]: Object.keys(propsForType).reduce(
                (menuAll, menuKey) => ({ ...menuAll, [menuKey]: props[menuKey] }),
                {},
            ),
        };
    }, {});

    const modifiedOnLogoClick = () => {
        if (isOnlyEndUser) return null;
        return onLogoClick();
    };

    return (
        <header
            data-app-header
            style={props.styles}
            data-user-role={props.priorizedUserRole}
            className={styles['global-header']}
        >
            <div>
                <Logo onClick={modifiedOnLogoClick} customNavLogo={customNavLogo} />
                <AppSwitcher
                    {...appSwitcherProps}
                    analyticsBeta={analyticsBeta}
                    active={activeApp}
                    isStaticActiveApp={isStaticActiveApp}
                    hasEndUserRole={hasEndUserRole}
                    isOnlyEndUser={isOnlyEndUser}
                />
            </div>
            {pendingJobsCount &&
            <div className={styles.flexbox}>
                <RetinaImage image={alert} isSmall />
                <div
                    className={styles['pending-jobs-banner']}
                >
                    <FormattedHTMLMessage
                        id="components.app_header.pending_jobs"
                        values={{ pendingJobsCount }}
                    />
                </div>
            </div>}

            <div className={styles['global-header-controls']}>
                {headerButtons.map(([component, propSource]) => React.createElement(component, { ...menuProps[propSource], key: propSource }),)}
            </div>
        </header>
    );
}

const menuPropTypesForAppHeader = Object.keys(menuPropTypes).reduce(
    (all, key) => ({ ...all, ...menuPropTypes[key] }),
    {},
);

AppHeader.displayName = displayName('AppHeader');

AppHeader.propTypes = {
    ...menuPropTypesForAppHeader,
    ...appSwitcherTabPropTypes,
    ...settingsPropTypes,
    buttons: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)),
    onLogoClick: PropTypes.func.isRequired,
    visibleMenus: PropTypes.object,
    customNavLogo: PropTypes.object,
    activeApp: PropTypes.string.isRequired,
    isStaticActiveApp: PropTypes.bool,
    analyticsBeta: PropTypes.bool.isRequired,
    styles: PropTypes.object,
    priorizedUserRole: PropTypes.string,
    hasEndUserRole: PropTypes.bool,
    isOnlyEndUser: PropTypes.bool,
};

AppHeader.defaultProps = {
    analyticsBeta: false,
    buttons: [],
    styles: {},
    customNavLogo: {},
    visibleMenus: {},
    priorizedUserRole: '',
    isStaticActiveApp: false,
    hasEndUserRole: false,
    isOnlyEndUser: false,
};
